import isString from 'lodash/isString';
import moment, { DATE_FORMAT } from '@/utils/moment-wrapper';
import { createTimeSeriesFromDate } from '@/common/time-series-picker/time-series-picker.util';
import { i18n_locale } from '@/utils/i18n';

export const MS_IN_TIME_UNITS = {
    WEEK: 604800000,
    DAY: 86400000,
    HOUR: 3600000,
    MINUTE: 60000,
    SECOND: 1000,
};

export const convertTimeToMs = (time, timeUnit) => {
    if (isNaN(time) || !isString(timeUnit)) return;

    const timeUnitInMs = MS_IN_TIME_UNITS[timeUnit.toUpperCase()];
    if (!timeUnitInMs) return;

    return time * timeUnitInMs;
};

export const convertMsToTime = (timeInMs, timeUnit) => {
    if (isNaN(timeInMs) || !isString(timeUnit)) return;

    const timeUnitInMs = MS_IN_TIME_UNITS[timeUnit.toUpperCase()];
    if (!timeUnitInMs) return;

    return timeInMs / timeUnitInMs;
};

function getPreviousDateRange (first, count) {
    const start = moment(first, { ignoreTimeZone: true });
    const previousEnd = start.clone().subtract(1, 'days');
    const previousStart = previousEnd.clone().subtract(count - 1, 'days');

    return {
        previousStart: previousStart.format(DATE_FORMAT.iso),
        previousEnd: previousEnd.format(DATE_FORMAT.iso),
    };
}

export const getPreviousTimeSeries = ({ spec }) => {
    const { previousStart, previousEnd } = getPreviousDateRange(spec.first, spec.count);

    const timeSeries = createTimeSeriesFromDate({ start: previousStart, end: previousEnd }, true);
    timeSeries.config.type = 'Custom';

    return timeSeries;
};

// "Same period last year": the comparison window has the same length as
// the primary, ending one year before the primary's end. Preserves the
// number-of-days so day-by-day overlays line up; matches Data Explorer's
// same-period-last-year semantics. (Distinct from "last year", which
// would be a full 365-day window regardless of the primary's size.)
function getSamePeriodLastYearDateRange (first, count) {
    const start = moment(first, { ignoreTimeZone: true });
    const previousStart = start.clone().subtract(1, 'years');
    const previousEnd = previousStart.clone().add(count - 1, 'days');

    return {
        previousStart: previousStart.format(DATE_FORMAT.iso),
        previousEnd: previousEnd.format(DATE_FORMAT.iso),
    };
}

export const getSamePeriodLastYearTimeSeries = ({ spec }) => {
    const { previousStart, previousEnd } = getSamePeriodLastYearDateRange(spec.first, spec.count);

    const timeSeries = createTimeSeriesFromDate({ start: previousStart, end: previousEnd }, true);
    timeSeries.config.type = 'Custom';

    return timeSeries;
};

// Catalog of supported date-comparison presets. Each key is the
// caller-facing identifier (used in props + persistence); each preset
// has a `shift` function that returns a comparison TimeSeries from a
// primary TimeSeries. Adding a new preset (e.g. "previous quarter")
// means dropping an entry here and a label in the consuming UI — no
// changes in the dispatch loop.
export const DATE_COMPARISON_PRESETS = {
    previousPeriod: {
        id: 'previousPeriod',
        shift: getPreviousTimeSeries,
    },
    samePeriodLastYear: {
        id: 'samePeriodLastYear',
        shift: getSamePeriodLastYearTimeSeries,
    },
};

export const DATE_COMPARISON_PRESET_IDS = Object.keys(DATE_COMPARISON_PRESETS);

let cachedRelativeTimeLocale;
let cachedRelativeTimeFormatter;

function getRelativeTimeFormatter (locale) {
    if (cachedRelativeTimeFormatter && cachedRelativeTimeLocale === locale) {
        return cachedRelativeTimeFormatter;
    }

    cachedRelativeTimeLocale = locale;
    cachedRelativeTimeFormatter = new Intl.RelativeTimeFormat(locale, { numeric: 'auto', style: 'narrow' });

    return cachedRelativeTimeFormatter;
}

/**
 * Format a timestamp relative to now (e.g. "2m ago").
 *
 * @param {Date|string|number|null|undefined} timestamp Date instance, ISO string, or millisecond epoch.
 * @param {string} [locale] Defaults to the active i18n locale.
 * @returns {string}
 */
export function formatRelativeTime (timestamp, locale = i18n_locale?.value) {
    if (timestamp == null) return '';

    const ms = timestamp instanceof Date ? timestamp.getTime() : new Date(timestamp).getTime();
    if (Number.isNaN(ms)) return '';

    const formatter = getRelativeTimeFormatter(locale);
    const diffSeconds = (Date.now() - ms) / 1000;

    if (diffSeconds < 60) return formatter.format(0, 'second');
    if (diffSeconds < 3600) return formatter.format(-Math.floor(diffSeconds / 60), 'minute');
    if (diffSeconds < 86400) return formatter.format(-Math.floor(diffSeconds / 3600), 'hour');

    return formatter.format(-Math.floor(diffSeconds / 86400), 'day');
}
